import React from 'react';
import { get } from 'lodash';
import { SocialColumn } from '../../columnSelectorDialog/hooks/useColumnDialog';
import { PostTypeInsights } from '@frontend/gql/social/graphql';
import { Row } from '@tanstack/react-table';
import { formatCurrency } from '@frontend/applications/Social/Analytics/hooks/useCurrency';
import Currency from '../columnDefs/Currency';
import MemberPostTypes from '../columnDefs/member-post-types';
import PostsPostTypes from '../columnDefs/posts-post-types';
import Percentage from '../columnDefs/Percentage';
import { formatDurationFromSeconds } from '../../../utils/format';

export const NETWORKS_TABLE_COLUMN_CONFIG_VERSION = 2; // Updated to version 2 for paid metrics

export interface NetworksTableColumn<T = any> extends SocialColumn {
  header: string;
  id: string;
  enableSorting?: boolean;
  enableGlobalFiltering?: boolean;
  hidden: boolean;
  order?: number;
  size?: number;
  minSize?: number;
  maxSize?: number;
  accessorFn?: (row: T) => unknown;
  accessorKey?: string; // accessorKey or accessorFn should be set
  cell: (props: { row: Row<T> }) => React.ReactNode;
  csvValue: (props: { data: T }) => string | null | undefined;
  meta?: {
    headerGroupBorder: {
      borderColor: string,
    },
  },
}

export const hydrateColumnsFromJson = (
  jsonColumns: Partial<NetworksTableColumn<PostTypeInsights>>[],
): NetworksTableColumn<PostTypeInsights>[] => {
  const cellMap = Object.fromEntries(defaultNetworksTableColumns.map((col) => [col.id, col.cell]));
  const csvMap = Object.fromEntries(defaultNetworksTableColumns.map((col) => [col.id, col.csvValue]));
  const accessorFnMap = Object.fromEntries(defaultNetworksTableColumns.filter((col) => col.accessorFn).map((col) => [col.id, col.accessorFn]));

  return jsonColumns.map((col) => ({
    ...col,
    cell: col.id && cellMap[col.id] ? cellMap[col.id] : () => null,
    csvValue: col.id && csvMap[col.id] ? csvMap[col.id] : () => '',
    accessorFn: col.id && accessorFnMap[col.id] ? accessorFnMap[col.id] : undefined,
  })) as NetworksTableColumn<PostTypeInsights>[];
};

const POST_TYPE_MAPPING: Record<string, string> = {
  'INSTAGRAM_POST': 'Instagram Post',
  'INSTAGRAM_REEL': 'Instagram Reel',
  'INSTAGRAM_STORY': 'Instagram Story',
  'INSTAGRAM_VIDEO': 'Instagram Video',
  'YOUTUBE_VIDEO': 'YouTube Video',
  'YOUTUBE_SHORT': 'YouTube Short',
  'TIKTOK_VIDEO': 'TikTok Video',
  'PINTEREST_PIN': 'Pinterest Pin',
  'FACEBOOK_POST': 'Facebook Post',
  'TWITTER_POST': 'Twitter Post',
  'BLOG_MENTION': 'Blog Mention',
  'BLOG_DEDICATED': 'Blog Post',
  'SNAPCHAT_STORY': 'Snapchat Story',
  'OTHER': 'Other'
};

const baseDefaultColumnIds = [
  'postType',
  'members',
  'postsCreated',
  'posts',
  'engagements',
  'engagementRate',
  'impressions',
  'potentialReach',
  'tmv'
];

// Helper function to add/remove paid and organic metrics from any column array
export const adjustNetworksColumnsForPaidMetrics = (columns: string[], includePaidMetrics: boolean): string[] => {
  const paidMetricsColumns = ['paidEngagements', 'paidImpressions'];
  const organicMetricsColumns = ['organicEngagements', 'organicImpressions'];
  
  if (includePaidMetrics) {
    // Add organic and paid metrics if not already present
    const result = [...columns];
    
    // Add organic engagements after engagements if not present
    if (!result.includes('organicEngagements')) {
      const engagementsIndex = result.indexOf('engagements');
      if (engagementsIndex !== -1) {
        result.splice(engagementsIndex + 1, 0, 'organicEngagements');
      }
    }
    
    // Add paid engagements after organic engagements (or after engagements if organic not present)
    if (!result.includes('paidEngagements')) {
      const organicEngagementsIndex = result.indexOf('organicEngagements');
      const engagementsIndex = result.indexOf('engagements');
      const insertIndex = organicEngagementsIndex !== -1 ? organicEngagementsIndex + 1 : engagementsIndex + 1;
      if (insertIndex > 0) {
        result.splice(insertIndex, 0, 'paidEngagements');
      }
    }
    
    // Add organic impressions after impressions if not present
    if (!result.includes('organicImpressions')) {
      const impressionsIndex = result.indexOf('impressions');
      if (impressionsIndex !== -1) {
        result.splice(impressionsIndex + 1, 0, 'organicImpressions');
      }
    }
    
    // Add paid impressions after organic impressions (or after impressions if organic not present)
    if (!result.includes('paidImpressions')) {
      const organicImpressionsIndex = result.indexOf('organicImpressions');
      const impressionsIndex = result.indexOf('impressions');
      const insertIndex = organicImpressionsIndex !== -1 ? organicImpressionsIndex + 1 : impressionsIndex + 1;
      if (insertIndex > 0) {
        result.splice(insertIndex, 0, 'paidImpressions');
      }
    }
    
    return result;
  } else {
    // Remove paid and organic metrics columns if present
    return columns.filter((col) => !paidMetricsColumns.includes(col) && !organicMetricsColumns.includes(col));
  }
};

export const getDefaultVisibleNetworkColumnIds = (includePaidMetrics: boolean = false): string[] => {
  return adjustNetworksColumnsForPaidMetrics(baseDefaultColumnIds, includePaidMetrics);
};

export const defaultNetworksTableColumns: NetworksTableColumn<PostTypeInsights>[] = [
  {
    header: 'Post Type',
    id: 'postType',
    accessorKey: 'postType',
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 1,
    minSize: 150,
    maxSize: 170,
    csvValue: ({ data }) => {
      const postType = get(data, 'postType');
      return POST_TYPE_MAPPING[postType?.toUpperCase() || ''] || postType;
    },
    cell: ({ row }) => {
      const postType = get(row.original, 'postType');
      const displayValue = POST_TYPE_MAPPING[postType?.toUpperCase() || ''] || postType;
      return <div>{displayValue}</div>;
    },
  },
  {
    header: 'Members',
    id: 'members',
    accessorKey: 'activeMembers',
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 2,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'activeMembers');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'activeMembers');
      const postType = get(row.original, 'postType');
      return <MemberPostTypes value={value ?? 0} postType={postType} />;
    },
  },
  {
    header: "Posts Created",
    id: "postsCreated",
    accessorKey: "postsCreated",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 3,
    minSize: 130,
    maxSize: 150,
    csvValue: ({ data }) => {
      const value = get(data, 'postsCreated');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'postsCreated');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: 'Active Posts',
    id: 'posts',
    accessorKey: 'activePosts',
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 4,
    minSize: 110,
    maxSize: 130,
    csvValue: ({ data }) => {
      const value = get(data, 'activePosts');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'activePosts');
      const postType = get(row.original, 'postType');
      return <PostsPostTypes value={value ?? 0} postType={postType} />;
    },
  },
  {
    header: "Engagement",
    id: "engagements",
    accessorKey: "engagements",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 5,
    minSize: 130,
    maxSize: 150,
    csvValue: ({ data }) => {
      const value = get(data, 'engagements');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'engagements');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Organic Engagements",
    id: "organicEngagements",
    accessorKey: "organicEngagements",
    enableSorting: true,
    hidden: true, // Hidden by default, shown when feature flag is enabled
    isFixed: false,
    order: 5.3,
    minSize: 180,
    maxSize: 200,
    csvValue: ({ data }) => {
      const value = get(data, 'organicEngagements');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'organicEngagements');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Paid Engagements",
    id: "paidEngagements",
    accessorKey: "paidEngagements",
    enableSorting: true,
    hidden: true, // Hidden by default, shown when feature flag is enabled
    isFixed: false,
    order: 5.5,
    minSize: 155,
    maxSize: 175,
    csvValue: ({ data }) => {
      const value = get(data, 'paidEngagements');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'paidEngagements');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Engagement Rate",
    id: "engagementRate",
    accessorKey: "engagementRate",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 6,
    minSize: 175,
    maxSize: 195,
    csvValue: ({ data }) => {
      return get(data, 'engagementRate')?.toString();
    },
    cell: ({ row }) => {
      const percentValue = get(row.original, 'engagementRate');
      return <Percentage value={percentValue} aria-label={`Engagement Rate: ${percentValue || 'Not available'}`} />;
    },
  },
  {
    header: "Likes",
    id: "likes",
    accessorKey: "likes",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 7,
    minSize: 75,
    maxSize: 95,
    csvValue: ({ data }) => {
      const value = get(data, 'likes');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'likes');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Comments",
    id: "comments",
    accessorKey: "comments",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 8,
    minSize: 110,
    maxSize: 130,
    csvValue: ({ data }) => {
      const value = get(data, 'comments');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'comments');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Saves",
    id: "saves",
    accessorKey: "saves",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 9,
    minSize: 80,
    maxSize: 100,
    csvValue: ({ data }) => {
      const value = get(data, 'saves');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'saves');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Shares",
    id: "shares",
    accessorKey: "shares",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 10,
    minSize: 80,
    maxSize: 100,
    csvValue: ({ data }) => {
      const value = get(data, 'shares');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'shares');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  
  {
    header: "Impressions",
    id: "impressions",
    accessorKey: "impressions",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 11,
    minSize: 115,
    maxSize: 135,
    csvValue: ({ data }) => {
      const value = get(data, 'impressions');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'impressions');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Organic Impressions",
    id: "organicImpressions",
    accessorKey: "organicImpressions",
    enableSorting: true,
    hidden: true, // Hidden by default, shown when feature flag is enabled
    isFixed: false,
    order: 11.3,
    minSize: 165,
    maxSize: 185,
    csvValue: ({ data }) => {
      const value = get(data, 'organicImpressions');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'organicImpressions');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Paid Impressions",
    id: "paidImpressions",
    accessorKey: "paidImpressions",
    enableSorting: true,
    hidden: true, // Hidden by default, shown when feature flag is enabled
    isFixed: false,
    order: 11.5,
    minSize: 140,
    maxSize: 160,
    csvValue: ({ data }) => {
      const value = get(data, 'paidImpressions');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'paidImpressions');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  
  {
    header: "Total Reach",
    id: "reach",
    accessorKey: "reach",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 12,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'reach');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'reach');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Potential Reach",
    id: "potentialReach",
    accessorKey: "potentialReach",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 13,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'potentialReach');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'potentialReach');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Views",
    id: "views",
    accessorKey: "views",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 14,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'views');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'views');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },
  {
    header: "Total View Time",
    id: "viewTimeTotalSeconds",
    accessorKey: "viewTimeTotalSeconds",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 15,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'viewTimeTotalSeconds');
      return value != null ? formatDurationFromSeconds(value) : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'viewTimeTotalSeconds');
      return <div>{value != null ? formatDurationFromSeconds(value) : '-'}</div>;
    },
  },
  {
    header: "Avg View Time",
    id: "viewTimeAvgSeconds",
    accessorKey: "viewTimeAvgSeconds",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 16,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'viewTimeAvgSeconds');
      return value != null ? formatDurationFromSeconds(value) : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'viewTimeAvgSeconds');
      return <div>{value != null ? formatDurationFromSeconds(value) : '-'}</div>;
    },
  },
  {
    header: "TMV",
    id: "tmv",
    accessorKey: "tmv",
    enableSorting: true,
    hidden: false,
    isFixed: false,
    order: 17,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'tmv');
      const formattedValue = value ? formatCurrency(value) : '';
      return formattedValue;
    },
    cell: ({ row }) => {
      const value = get(row.original, 'tmv');
      return <Currency value={value ?? 0} aria-label={`TMV: ${value ?? 0}`} />;
    },
  },
  {
    header: "Follower Growth",
    id: "followerGrowth",
    accessorKey: "followerGrowth",
    enableSorting: true,
    hidden: true,
    isFixed: false,
    order: 18,
    minSize: 100,
    maxSize: 120,
    csvValue: ({ data }) => {
      const value = get(data, 'followerGrowth');
      return value != null ? value.toLocaleString() : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'followerGrowth');
      return <div>{value != null ? value.toLocaleString() : '-'}</div>;
    },
  },

];
